import React, { useEffect, useState } from "react";
import { Button } from "bootstrap-4-react/lib/components";
import Table from "react-bootstrap/Table";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import LoaderSpinner from "../common/Loader";
import { getDropshipSetting, upsertDropshipSetting, getDropshipProductHistory } from "../../store/actions/dropship.action";

const HISTORY_PAGE_SIZE = 30;

// Human-readable labels for the audited columns.
const FIELD_LABELS = {
    WholesalePrice: "Wholesale Price",
    AvailableQuantity: "Quantity",
    ShippingPaidBy: "Shipping Paid By",
    FulfillmentDays: "Fulfillment Days",
    ReturnPolicy: "Return Policy",
    IsListed: "Listed",
};

// Display a stored value: prices get a $, the IsListed bit becomes Listed/Unlisted.
const formatHistoryValue = (columnName, value) => {
    if (value === null || value === undefined || value === "") return "—";
    if (columnName === "IsListed") return value === "1" || value === "True" || value === true ? "Listed" : "Unlisted";
    if (columnName === "WholesalePrice") return `$${value}`;
    return String(value);
};

export default function DropshipSettings() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userDetails = useSelector((state) => state.auth.user);
    const { setting, settingLoading, productHistory, productHistoryStatus, productHistoryTotalRecords } = useSelector(
        (state) => state.dropshipReducer
    );
    const [isEnabled, setIsEnabled] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [historyPage, setHistoryPage] = useState(1);

    useEffect(() => {
        if (userDetails?.Value) {
            dispatch(getDropshipSetting({}));
        }
    }, [userDetails]);

    // Seller-wide change history (no dropshipProductId filter). Paged.
    useEffect(() => {
        if (userDetails?.Value) {
            dispatch(getDropshipProductHistory({ pageIndex: historyPage, pageSize: HISTORY_PAGE_SIZE }));
        }
    }, [userDetails, historyPage]);

    const historyTotalPages = Math.max(1, Math.ceil((productHistoryTotalRecords || 0) / HISTORY_PAGE_SIZE));

    useEffect(() => {
        if (setting) {
            setIsEnabled(setting.isDropshipEnabled || false);
        }
    }, [setting]);

    const handleAction = () => {
        const newValue = !isEnabled;
        setSubmitting(true);
        dispatch(upsertDropshipSetting({ isDropshipEnabled: newValue }))
            .then(() => {
                setIsEnabled(newValue);
                toast.success(
                    newValue
                        ? "Dropshipping enabled. Explore menu is now available."
                        : "Dropshipping disabled."
                );
            })
            .catch(() => {
                toast.error("Failed to update dropshipping setting.");
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const getButtonLabel = () => {
        if (submitting) return "Saving...";
        return isEnabled ? "Disable Dropshipping" : "Enable Dropshipping";
    };

    return (
        <main>
            <div className="main__container">
                <div className="main__tile_heading cus-btn-include-sec">
                    Dropshipping Settings
                    <div className="top_right_button_container">
                        <Button
                            className="cus-primary-transparent-btn"
                            onClick={() => navigate("/setting")}
                        >
                            Back
                        </Button>
                    </div>
                </div>

                {(settingLoading || submitting) && (
                    <div className="full-screen-spinner-overlay">
                        <LoaderSpinner />
                    </div>
                )}

                <div className="main_content_table">
                    <div className="row" style={{ padding: "24px", margin: 0 }}>
                        <div className="col-lg-8 col-md-12 col-sm-12">
                            <h5 style={{ marginBottom: 8 }}>Dropshipping Status</h5>
                            <p style={{ color: "#666", fontSize: 14, marginBottom: 16 }}>
                                When enabled, you can browse and import products from other sellers
                                to resell on your own storefront. Other sellers can also browse and
                                import your listed products. You can change this anytime.
                            </p>
                            <div style={{ marginBottom: 8 }}>
                                <strong>Current status:</strong>{" "}
                                <span style={{ color: isEnabled ? "#28a745" : "#dc3545", fontWeight: 600 }}>
                                    {isEnabled ? "Enabled" : "Disabled"}
                                </span>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-12 col-sm-12 d-flex align-items-center justify-content-end">
                            <Button
                                className={isEnabled ? "cus-primary-transparent-btn" : "cus-seconday-bg-btn"}
                                onClick={handleAction}
                                disabled={submitting || settingLoading}
                            >
                                {getButtonLabel()}
                            </Button>
                        </div>
                    </div>

                </div>

                {/* ── Change History: every edit a wholesaler makes to a dropship listing ── */}
                <div className="main_content_table" style={{ marginTop: 24 }}>
                    <div className="row" style={{ padding: "16px 24px", margin: 0 }}>
                        <div className="col-12">
                            <h5 style={{ marginBottom: 4 }}>Listing Change History</h5>
                            <p style={{ color: "#666", fontSize: 14, marginBottom: 0 }}>
                                A record of every change made to your dropship listings — price, quantity,
                                terms, and list/unlist actions.
                            </p>
                        </div>
                    </div>

                    <Table responsive className="tableListing">
                        <thead>
                            <tr>
                                <th>Product</th>
                                <th>Field</th>
                                <th>Old Value</th>
                                <th>New Value</th>
                                <th>Action</th>
                                <th>Changed By</th>
                                <th>When</th>
                            </tr>
                        </thead>
                        <tbody>
                            {productHistoryStatus === "PENDING" && (
                                <tr><td colSpan={7} className="text-center">Loading…</td></tr>
                            )}
                            {productHistoryStatus !== "PENDING" && (productHistory || []).length === 0 && (
                                <tr><td colSpan={7} className="text-center">No changes recorded yet.</td></tr>
                            )}
                            {(productHistory || []).map((h) => (
                                <tr key={h.id}>
                                    <td>
                                        <strong>{h.productSKU}</strong>
                                        {h.productName ? ` - ${h.productName}` : ""}
                                    </td>
                                    <td>{FIELD_LABELS[h.columnName] || h.columnName}</td>
                                    <td>{formatHistoryValue(h.columnName, h.oldValue)}</td>
                                    <td>{formatHistoryValue(h.columnName, h.newValue)}</td>
                                    <td>{h.action}</td>
                                    <td>{h.changedByName || "—"}</td>
                                    <td>{h.createdDate ? moment.utc(h.createdDate).local().format("MMM D, YYYY h:mm A") : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    {productHistoryTotalRecords > HISTORY_PAGE_SIZE && (
                        <div className="d-flex align-items-center justify-content-end" style={{ padding: "12px 24px", gap: 12 }}>
                            <Button
                                className="cus-primary-transparent-btn"
                                onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                                disabled={historyPage <= 1}
                            >
                                Previous
                            </Button>
                            <span style={{ fontSize: 13, color: "#666" }}>
                                Page {historyPage} of {historyTotalPages}
                            </span>
                            <Button
                                className="cus-primary-transparent-btn"
                                onClick={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))}
                                disabled={historyPage >= historyTotalPages}
                            >
                                Next
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
}
